import { AnyAction } from '@reduxjs/toolkit';
import { parseInt } from 'common/helpers';
import {
  GuideTypes,
  IGuide,
  IStickyPoints,
  KeyCodeTypes,
  PointPositionTypes,
} from 'common/interfaces';
import { Dispatch } from 'react';
import { copyElemWhenDragAndDrop } from 'store/reducers/elems.slice';
import { copyGuide } from 'store/reducers/guides.slice';

import CoordsService from './CoordsService';
import KeyboardService from './KeyboardService';

export class DragAndDropService {
  needToCopy: boolean;
  shiftX: number;
  shiftY: number;
  stickyPoints: IStickyPoints[];
  stickingDist: number;
  stickyHorizontalLine: HTMLElement;
  stickyVerticalLine: HTMLElement;

  constructor(
    readonly id: string,
    readonly domElem: HTMLElement,
    readonly event: MouseEvent,
    readonly pressedKeys: string[],
    stickyPoints: IStickyPoints[],
    readonly dispatch: Dispatch<AnyAction>,
  ) {
    const savedTransform = this.domElem.style.transform;
    this.domElem.style.transform = '';

    this.shiftX =
      this.event.clientX - this.domElem.getBoundingClientRect().left;
    this.shiftY = this.event.clientY - this.domElem.getBoundingClientRect().top;

    this.domElem.style.transform = savedTransform;

    this.needToCopy = KeyboardService.checkIfPressedOneKeyOf(pressedKeys, [
      KeyCodeTypes.ALT_LEFT,
      KeyCodeTypes.ALT_RIGHT,
    ]);

    this.stickyPoints = stickyPoints.filter((item) => item.id !== id);
    this.stickingDist = 10;

    const horizontalLine = document.createElement('div');
    horizontalLine.style.position = 'absolute';
    horizontalLine.style.background = 'red';
    horizontalLine.style.zIndex = '5000';
    document.body.append(horizontalLine);
    this.stickyHorizontalLine = horizontalLine;

    const verticalLine = document.createElement('div');
    verticalLine.style.position = 'absolute';
    verticalLine.style.background = 'red';
    verticalLine.style.zIndex = '5000';
    document.body.append(verticalLine);
    this.stickyVerticalLine = verticalLine;
  }

  _isValueInsideStickyZone(value: number, stickyPointValue: number): boolean {
    return (
      value > stickyPointValue - this.stickingDist &&
      value < stickyPointValue + this.stickingDist
    );
  }

  _addHorizontalLineStyles(
    pointsData: IStickyPoints,
    stickyValue: number,
  ): void {
    this.stickyHorizontalLine.style.display = 'block';
    this.stickyHorizontalLine.style.left = stickyValue + 'px';
    this.stickyHorizontalLine.style.top =
      Math.min(...pointsData.yPoints) + 'px';
    this.stickyHorizontalLine.style.width = '1px';
    this.stickyHorizontalLine.style.height =
      Math.max(...pointsData.yPoints) - Math.min(...pointsData.yPoints) + 'px';
  }

  _addVerticalLineStyles(pointsData: IStickyPoints, stickyValue: number): void {
    this.stickyVerticalLine.style.display = 'block';
    this.stickyVerticalLine.style.left = Math.min(...pointsData.xPoints) + 'px';
    this.stickyVerticalLine.style.top = stickyValue + 'px';
    this.stickyVerticalLine.style.height = '1px';
    this.stickyVerticalLine.style.width =
      Math.max(...pointsData.xPoints) - Math.min(...pointsData.xPoints) + 'px';
  }

  _handleElemSticking(
    left: number,
    top: number,
  ): { leftValue: number; topValue: number } {
    let leftValue = left;
    let topValue = top;

    const rightValue = leftValue + parseInt(this.domElem.style.width);
    const bottomValue = topValue + parseInt(this.domElem.style.height);

    const centerXValue = leftValue + parseInt(this.domElem.style.width) / 2;
    const centerYValue = topValue + parseInt(this.domElem.style.height) / 2;

    let isLineHidden = true;
    let isVerticalLineHidden = true;

    let ignoreVerticalGuidesSticking = false;
    let ignoreHorizontalGuidesSticking = false;

    for (const pointsData of this.stickyPoints) {
      const isGuidePointsData = pointsData.xPoints.length === 1;

      if (
        !isGuidePointsData &&
        (leftValue > Math.max(...pointsData.xPoints) + this.stickingDist ||
          rightValue < Math.min(...pointsData.xPoints) - this.stickingDist ||
          bottomValue < Math.min(...pointsData.yPoints) - this.stickingDist ||
          topValue > Math.max(...pointsData.yPoints) + this.stickingDist)
      ) {
        continue;
      }

      if (
        isGuidePointsData &&
        ((pointsData.xPoints[0] &&
          (rightValue < pointsData.xPoints[0] - this.stickingDist ||
            leftValue > pointsData.xPoints[0] + this.stickingDist)) ||
          (pointsData.yPoints[0] &&
            (bottomValue < pointsData.yPoints[0] - this.stickingDist ||
              topValue > pointsData.yPoints[0] + this.stickingDist)))
      ) {
        continue;
      }

      for (const stickyValue of pointsData.xPoints) {
        if (ignoreVerticalGuidesSticking) break;

        if (this._isValueInsideStickyZone(leftValue, stickyValue)) {
          this._addHorizontalLineStyles(pointsData, stickyValue);
          isLineHidden = false;
          ignoreVerticalGuidesSticking = true;

          leftValue = stickyValue;

          break;
        }

        if (this._isValueInsideStickyZone(centerXValue, stickyValue)) {
          this._addHorizontalLineStyles(pointsData, stickyValue);
          isLineHidden = false;
          ignoreVerticalGuidesSticking = true;

          const offset = stickyValue - centerXValue;
          leftValue = leftValue + offset;
          break;
        }

        if (this._isValueInsideStickyZone(rightValue, stickyValue)) {
          this._addHorizontalLineStyles(pointsData, stickyValue);
          isLineHidden = false;
          ignoreVerticalGuidesSticking = true;

          const offset = stickyValue - rightValue;
          leftValue = leftValue + offset;
          break;
        }

        ignoreVerticalGuidesSticking = false;
      }

      for (const stickyValue of pointsData.yPoints) {
        if (ignoreHorizontalGuidesSticking) break;

        if (this._isValueInsideStickyZone(topValue, stickyValue)) {
          this._addVerticalLineStyles(pointsData, stickyValue);
          isVerticalLineHidden = false;
          ignoreHorizontalGuidesSticking = true;

          topValue = stickyValue;
          break;
        }

        if (this._isValueInsideStickyZone(centerYValue, stickyValue)) {
          this._addVerticalLineStyles(pointsData, stickyValue);
          isVerticalLineHidden = false;
          ignoreHorizontalGuidesSticking = true;

          const offset = stickyValue - centerYValue;
          topValue = topValue + offset;
          break;
        }

        if (this._isValueInsideStickyZone(bottomValue, stickyValue)) {
          this._addVerticalLineStyles(pointsData, stickyValue);
          isVerticalLineHidden = false;
          ignoreHorizontalGuidesSticking = true;

          const offset = stickyValue - bottomValue;
          topValue = topValue + offset;
          break;
        }

        ignoreHorizontalGuidesSticking = false;
      }
    }

    if (isLineHidden) {
      this.stickyHorizontalLine.style.display = 'none';
    }

    if (isVerticalLineHidden) {
      this.stickyVerticalLine.style.display = 'none';
    }

    return { leftValue, topValue };
  }

  addListenersWithFinishCallback(
    handler: (event: MouseEvent) => void,
    cb: () => void,
  ): void {
    document.addEventListener('mousemove', handler);

    const removeLines = (): void => {
      this.stickyHorizontalLine.remove();
      this.stickyVerticalLine.remove();
    };

    document.onmouseup = function () {
      document.removeEventListener('mousemove', handler);
      removeLines();
      cb();
      document.onmouseup = null;
    };
  }

  moveAt(
    pageX: number | null,
    pageY: number | null,
    copyHandler = () => this.dispatch(copyElemWhenDragAndDrop(this.id)),
  ): void {
    if (this.needToCopy) {
      copyHandler();
      this.needToCopy = false;
    }

    const initialLeft = pageX !== null ? pageX - this.shiftX : 0;
    const initialTop = pageY !== null ? pageY - this.shiftY : 0;

    const { leftValue, topValue } = this._handleElemSticking(
      initialLeft,
      initialTop,
    );

    if (pageX !== null) {
      this.domElem.style.left = leftValue + 'px';
    }

    if (pageY !== null) {
      this.domElem.style.top = topValue + 'px';
    }
  }

  addSelectionElemAndGetResizeHandler(): (
    pageX: number,
    pageY: number,
  ) => void {
    this.domElem.classList.add('selection-area');

    this.domElem.style.left = this.event.pageX + 'px';
    this.domElem.style.top = this.event.pageY + 'px';

    const startPoint = { x: this.event.pageX, y: this.event.pageY };

    document.body.append(this.domElem);

    const setSelectionAreaSize = (pageX: number, pageY: number): void => {
      const cursorPoint = { x: pageX, y: pageY };

      const handlerPosition = CoordsService.getPositionRelativeToPoint(
        startPoint,
        cursorPoint,
      );

      if (handlerPosition === PointPositionTypes.BOTTOM_RIGHT) {
        this.domElem.style.width = pageX - startPoint.x + 'px';
        this.domElem.style.height = pageY - startPoint.y + 'px';
        this.domElem.style.left = startPoint.x + 'px';
        this.domElem.style.top = startPoint.y + 'px';
      } else if (handlerPosition === PointPositionTypes.BOTTOM_LEFT) {
        this.domElem.style.width = startPoint.x - pageX + 'px';
        this.domElem.style.height = pageY - startPoint.y + 'px';
        this.domElem.style.left = pageX + 'px';
        this.domElem.style.top = startPoint.y + 'px';
      } else if (handlerPosition === PointPositionTypes.TOP_LEFT) {
        this.domElem.style.width = startPoint.x - pageX + 'px';
        this.domElem.style.height = startPoint.y - pageY + 'px';
        this.domElem.style.left = pageX + 'px';
        this.domElem.style.top = pageY + 'px';
      } else if (handlerPosition === PointPositionTypes.TOP_RIGHT) {
        this.domElem.style.width = pageX - startPoint.x + 'px';
        this.domElem.style.height = startPoint.y - pageY + 'px';
        this.domElem.style.left = startPoint.x + 'px';
        this.domElem.style.top = pageY + 'px';
      }
    };

    return setSelectionAreaSize;
  }

  getMoveMultiSelectionHandler(
    changeStateCallback: (leftOffset: number, topOffset: number) => void,
  ): (pageX: number, pageY: number) => void {
    const shiftX =
      this.event.clientX - this.domElem.getBoundingClientRect().left;
    const shiftY =
      this.event.clientY - this.domElem.getBoundingClientRect().top;

    let prevPosition = { left: this.event.pageX, top: this.event.pageY };

    // TODO: возможно стоит переписать, чтобы можно было активировать прилипание и prevPosition запихнуть в this
    const moveHandler = (pageX: number, pageY: number): void => {
      const leftOffset = pageX - prevPosition.left;
      const topOffset = pageY - prevPosition.top;

      this.domElem.style.left = pageX - shiftX + 'px';
      this.domElem.style.top = pageY - shiftY + 'px';

      changeStateCallback(leftOffset, topOffset);

      prevPosition = {
        left: pageX,
        top: pageY,
      };
    };

    return moveHandler;
  }

  getMoveSelectedGuidesHandler(
    changeStateCallback: (leftOffset: number, topOffset: number) => void,
  ): (pageX: number, pageY: number) => void {
    let prevPosition = { left: this.event.pageX, top: this.event.pageY };

    // TODO: возможно стоит переписать, чтобы можно было активировать прилипание и prevPosition запихнуть в this
    const moveHandler = (pageX: number, pageY: number): void => {
      const leftOffset = pageX - prevPosition.left;
      const topOffset = pageY - prevPosition.top;

      changeStateCallback(leftOffset, topOffset);

      prevPosition = {
        left: pageX,
        top: pageY,
      };
    };

    return moveHandler;
  }

  getMoveGuideHandler(
    guideData: IGuide,
  ): (pageX: number, pageY: number) => void {
    const copyGuideHandler = (): void => this.dispatch(copyGuide(guideData));

    const moveHandler = (pageX: number, pageY: number): void => {
      const xValue = guideData.type === GuideTypes.HORIZONTAL ? null : pageX;
      const yValue = guideData.type === GuideTypes.HORIZONTAL ? pageY : null;

      this.moveAt(xValue, yValue, copyGuideHandler);
    };

    return moveHandler;
  }
}
